import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import API_URLS from '../../../core/common/apiUrls';
import { getCurrentUser } from '../../../core/common/authSession';
import './flightDetails.scss';
import { all_routes } from '../../router/all_routes';
import AirplaneLoader from '../flight-loader/flightLoader';

// TypeScript Interfaces
interface PassengerFormData {
    Title: string;
    FirstName: string;
    LastName: string;
    PaxType: number;
    Gender: number;
    DateOfBirth: string;
    Nationality: string;
    CityOfResidence: string;
    ContactNo: string;
    Email: string;
    IsLeadPax: boolean;

    // Insurance fields (shown/used only when insurance is selected)
    PassportNo?: string;
    PassportCountry?: string;
    AddressLine1?: string;
    AddressLine2?: string;
    CityCode?: string;
    CountryCode?: string;
    PinCode?: string;
    BeneficiaryTitle?: string;
    BeneficiaryName?: string;
    RelationShipToInsured?: string;
    RelationToBeneficiary?: string;
    MajorDestination?: string;
}

interface Airport {
    AirportCode: string;
    AirportName: string;
    CityCode: string;
    CityName: string;
    CountryCode: string;
    CountryName: string;
    Terminal: string;
}

interface Segment {
    Airline: {
        AirlineCode: string;
        AirlineName: string;
        FareClass: string;
        FlightNumber: string;
        OperatingCarrier: string;
    };
    Baggage: string;
    CabinBaggage: string;
    CabinClass: number;
    Craft: string;
    Destination: {
        Airport: Airport;
        ArrTime: string;
    };
    Duration: number;
    Origin: {
        Airport: Airport;
        DepTime: string;
    };
    StopOver: boolean;
}

interface FareBreakdown {
    AdditionalTxnFeeOfrd: number;
    AdditionalTxnFeePub: number;
    BaseFare: number;
    Currency: string;
    PGCharge: number;
    PassengerCount: number;
    PassengerType: number;
    SupplierReissueCharges: number;
    Tax: number;
    TaxBreakUp: Array<{ key: string; value: number }> | null;
    YQTax: number;
}

interface Fare {
    AdditionalTxnFeeOfrd: number;
    AdditionalTxnFeePub: number;
    BaseFare: number;
    ChargeBU: Array<{ key: string; value: number }>;
    CommissionEarned: number;
    Currency: string;
    Discount: number;
    IncentiveEarned: number;
    OfferedFare: number;
    OtherCharges: number;
    PGCharge: number;
    PLBEarned: number;
    PublishedFare: number;
    ServiceFee: number;
    ServiceFeeDisplayType: number;
    Tax: number;
    TaxBreakup: Array<{ key: string; value: number }>;
    TdsOnCommission: number;
    TdsOnIncentive: number;
    TdsOnPLB: number;
    TotalBaggageCharges: number;
    TotalMealCharges: number;
    TotalSeatCharges: number;
    TotalSpecialServiceCharges: number;
    YQTax: number;
}

interface FlightResult {
    AirlineCode: string;
    AirlineName?: string;
    AirlineRemark: string;
    Fare: Fare;
    FareBreakdown: FareBreakdown[];
    FareClassification: {
        Color: string;
        Type: string;
    };
    FareInclusions: any[];
    FareRules: Array<{
        Airline: string;
        Destination: string;
        FareBasisCode: string;
        FareFamilyCode: string;
        FareRestriction: string;
        FareRuleDetail: string;
        FareRuleIndex: string;
        Origin: string;
    }>;
    FirstNameFormat: string;
    GSTAllowed: boolean;
    IsBookableIfSeatNotAvailable: boolean;
    IsCouponAppilcable: boolean;
    IsFreeMealAvailable: boolean;
    IsGSTMandatory: boolean;
    IsHoldAllowedWithSSR: boolean;
    IsHoldMandatoryWithSSR: boolean;
    IsLCC: boolean;
    IsPanRequiredAtBook: boolean;
    IsPanRequiredAtTicket: boolean;
    IsPassportFullDetailRequiredAtBook: boolean;
    IsPassportRequiredAtBook: boolean;
    IsPassportRequiredAtTicket: boolean;
    IsRefundable: boolean;
    LastNameFormat: string;
    LastTicketDate: string | null;
    MiniFareRules: any[][];
    RequiredFieldValidators: {
        IsSeatRequired: boolean;
    };
    ResultFareType: string;
    ResultIndex: string;
    Segments: Segment[][];
    Source: number;
    TicketAdvisory: string | null;
    ValidatingAirline: string;
}

interface SelectedOption {
    amenities: Array<{
        code: string;
        description: string;
        pricePerPax: number;
        total: number;
        type: string;
    }>;
    amenityTotal: number;
    baggageOption: string;
    baseFare: number;
    description: string;
    mealOption: string;
    name: string;
    optionId: string;
    totalFare: number;
}

interface FareDetails {
    OfferedFare: number;
    OptionsTotal: number;
    OriginalFare: number;
    Results: FlightResult[];
    SelectedOptions: SelectedOption[];
}

interface InsurancePlan {
    CoverageDetails: Array<{
        Coverage: string;
        Excess: any;
        SumCurrency: string;
        SumInsured: string;
    }>;
    PlanCategory: number;
    PlanCode: string;
    PlanCoverage: number;
    PlanDescription: string | null;
    PlanName: string;
    PlanType: number;
    PolicyEndDate: string;
    PolicyStartDate: string;
    PoweredBy?: string; // Add this optional property
    PremiumList: Array<{
        CancellationCharge: number;
        Commission: number;
        CustomerPrice: number;
        MaxAge: number;
        MinAge: number;
        PassengerCount: number;
        Premium: number;
        Price: {
            CommissionEarned: number;
            Currency: string;
            GrossFare: number;
            KrishiKalyanTax: number;
            OfferedPrice: number;
            OfferedPriceRoundedOff: number;
            PublishedPrice: number;
            PublishedPriceRoundedOff: number;
            ServiceTax: number;
            SwachhBharatTax: number;
            TdsOnCommission: number;
        };
    }>;
    Price: {
        CommissionEarned: number;
        Currency: string;
        GrossFare: number;
        KrishiKalyanTax: number;
        OfferedPrice: number;
        OfferedPriceRoundedOff: number;
        PublishedPrice: number;
        PublishedPriceRoundedOff: number;
        ServiceTax: number;
        SwachhBharatTax: number;
        TdsOnCommission: number;
    };
    ResultIndex: number;
    SumInsured: string;
    SumInsuredCurrency: string;
}

interface FlightDetailsResponse {
    Fare: FareDetails;
    FareRules: Array<{
        Airline: string;
        DepartureTime: string;
        Destination: string;
        FareBasisCode: string;
        FareInclusions: string[];
        FareRestriction: any;
        FareRuleDetail: string;
        FlightId: number;
        Origin: string;
        ReturnDate: string;
    }>;
    FlightOfferIds: number[];
    Insurance: InsurancePlan[];
    InsuranceError: any;
    ResultIndexes: string[];
    TraceId: string;
}

interface ApiError {
    ErrorCode: number;
    ErrorMessage: string;
}

interface ApiResponse<T> {
    Response?: {
        Error?: ApiError;
        Result?: T;
        ResponseStatus?: number;
        TraceId?: string;
    };
}

interface InsuranceSearchRequest {
    PlanCategory: number;
    PlanType: number;
    PlanCoverage: number;
    TravelStartDate: string;
    NoOfPax: number;
    PaxAge: number[];
    EndUserIp: string;
}

interface InsuranceSearchResponse {
    Response: {
        Error: {
            ErrorCode: number;
            ErrorMessage: string;
        };
        ResponseStatus: number;
        Results: InsurancePlan[];
        TraceId: string;
    };
}

interface InsuranceBookingRequest {
    EndUserIp: string;
    TraceId: string;
    GenerateInsurancePolicy: string;
    ResultIndex: number;
    Passenger: Array<{
        Title: string;
        FirstName: string;
        LastName: string;
        BeneficiaryTitle: string;
        BeneficiaryName: string;
        RelationShipToInsured: string;
        RelationToBeneficiary: string;
        Gender: string;
        Sex: number;
        DOB: string;
        PassportNo: string;
        PassportCountry: string;
        PhoneNumber: string;
        EmailId: string;
        AddressLine1: string;
        AddressLine2: string;
        CityCode: string;
        CountryCode: string;
        MajorDestination: string;
        PinCode: number;
    }>;
}

const FlightDetails = () => {
    const routes = all_routes;
    const location = useLocation();
    const navigate = useNavigate();
    const isHoldMode: boolean = location.state?.isHoldMode ?? false;
    const isDomestic: boolean = location.state?.isDomestic ?? true;
    const [apiResponse, setApiResponse] = useState<FlightDetailsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [passengers, setPassengers] = useState<PassengerFormData[]>([]);
    const [depositPerPax, setDepositPerPax] = useState<number>(isDomestic ? 250 : 500);
    const [savingPassengers, setSavingPassengers] = useState(false);
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        baggageInfo: false,
        amenities: false,
        insurance: false,
        fareRules: false
    });

    // Insurance states
    const [includeInsurance, setIncludeInsurance] = useState(false);
    const [insurancePlans, setInsurancePlans] = useState<InsurancePlan[]>([]);
    const [selectedInsurancePlan, setSelectedInsurancePlan] = useState<InsurancePlan | null>(null);
    const [searchingInsurance, setSearchingInsurance] = useState(false);
    const [insuranceError, setInsuranceError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        fetch(API_URLS.FLIGHTS.LOCK_PRICE_CONFIG)
            .then(r => r.json())
            .then(d => {
                const rate = isDomestic ? d?.domestic_deposit_per_pax : d?.international_deposit_per_pax;
                if (rate) setDepositPerPax(Number(rate));
            })
            .catch(() => {});
    }, [isDomestic]);

    useEffect(() => {
        window.scrollTo(0, 0);
        const fetchFlightDetails = async () => {
            try {
                if (!location.state) {
                    console.error('No location state found!');
                    setError('No flight parameters available. Please search for a flight first.');
                    setLoading(false);
                    return;
                }

                const requestParams = location.state;
                // Build request body based on flight type
                let requestBody: any = {
                    EndUserIp: requestParams.EndUserIp || "106.219.87.88",
                    TraceId: requestParams.TraceId,
                    PassengerCount: requestParams.PassengerCount || 1,
                    TravelStartDate: requestParams.TravelStartDate
                };

                if (requestParams.isRoundTrip) {
                    // Round trip request
                    requestBody.ResultIndexOB = requestParams.ResultIndexOB;
                    requestBody.ResultIndexIB = requestParams.ResultIndexIB;
                    requestBody.OptionIds = [
                        requestParams.selectedOutboundOption?.optionId,
                        requestParams.selectedReturnOption?.optionId
                    ].filter(Boolean);
                } else {
                    // One-way request
                    requestBody.ResultIndex = requestParams.ResultIndex;
                    requestBody.optionId = requestParams.optionId;
                }

                const response = await fetch(API_URLS.FLIGHTS.FLIGHT_DETAILS, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(requestBody)
                });

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                const data: FlightDetailsResponse = await response.json();
                setApiResponse(data || null);

                // Initialize passenger forms
                const passengerCount = requestParams.PassengerCount || 1;
                const storedUser = getCurrentUser() as Record<string, any> | null;
                const leadFirstName = storedUser?.first_name || (storedUser?.name ? storedUser.name.trim().split(/\s+/)[0] : '') || '';
                const leadLastName = storedUser?.last_name || (storedUser?.name ? storedUser.name.trim().split(/\s+/).slice(1).join(' ') : '') || '';
                const leadEmail = storedUser?.email || '';
                const leadPhone = (() => {
                    const digits = (storedUser?.phone || '').replace(/\D/g, '');
                    return digits.length >= 10 ? digits.slice(-10) : digits;
                })();
                const initialPassengers: PassengerFormData[] = Array.from({ length: passengerCount }, (_, index) => ({
                    Title: 'Mr',
                    FirstName: index === 0 ? leadFirstName : '',
                    LastName: index === 0 ? leadLastName : '',
                    PaxType: 1,
                    Gender: 1,
                    DateOfBirth: '1990-01-01',
                    Nationality: 'IN',
                    CityOfResidence: '',
                    ContactNo: index === 0 ? leadPhone : '',
                    Email: index === 0 ? leadEmail : '',
                    IsLeadPax: index === 0,
                    PassportNo: '',
                    PassportCountry: 'IND',
                    AddressLine1: '',
                    AddressLine2: '',
                    CityCode: '',
                    CountryCode: 'IND',
                    PinCode: '',
                    BeneficiaryTitle: 'Mr',
                    BeneficiaryName: '',
                    RelationShipToInsured: 'Self',
                    RelationToBeneficiary: 'Father',
                    MajorDestination: ''
                }));
                setPassengers(initialPassengers);

            } catch (err: any) {
                console.error('Error fetching flight details:', err);
                setError(err.message || 'Failed to load flight details');
            } finally {
                setLoading(false);
            }
        };

        fetchFlightDetails();
    }, [location.state, navigate]);

    // Search insurance when checkbox is checked (only once per toggle / flight)
    useEffect(() => {
        const searchInsurance = async () => {
            if (!includeInsurance || !apiResponse || !location.state) return;

            setSearchingInsurance(true);
            setInsuranceError(null);

            try {
                const requestParams = location.state;

                // You can still calculate ages from the current passengers snapshot.
                const passengerAges = passengers.map(pax => {
                    const birthDate = dayjs(pax.DateOfBirth);
                    const today = dayjs();
                    return today.diff(birthDate, 'year');
                });

                const searchRequest: InsuranceSearchRequest = {
                    PlanCategory: 1,
                    PlanType: 1,
                    PlanCoverage: 4,
                    TravelStartDate: requestParams.TravelStartDate,
                    NoOfPax: passengers.length,
                    PaxAge: passengerAges,
                    EndUserIp: requestParams.EndUserIp || '106.219.87.88',
                };

                const response = await fetch(API_URLS.FLIGHTS.INSURANCE.SEARCH_INSURANCE, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(searchRequest),
                });

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                const data: InsuranceSearchResponse = await response.json();

                if (data.Response.ResponseStatus === 1 && data.Response.Results) {
                    setInsurancePlans(data.Response.Results);

                    // Only set default once if nothing is selected yet
                    if (!selectedInsurancePlan && data.Response.Results.length > 0) {
                        setSelectedInsurancePlan(data.Response.Results[0]);
                    }
                } else {
                    throw new Error(
                        data.Response.Error?.ErrorMessage || 'Failed to fetch insurance plans'
                    );
                }
            } catch (err: any) {
                console.error('Error searching insurance:', err);
                setInsuranceError(err.message || 'Failed to load insurance options');
            } finally {
                setSearchingInsurance(false);
            }
        };

        searchInsurance();
    }, [includeInsurance, apiResponse, location.state]);


    const toggleSection = (section: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const isValidPhone = (phone: string) => {
        // Indian 10-digit mobile starting 6–9
        return /^[6-9]\d{9}$/.test(phone.trim());
    };

    const isValidEmail = (email: string) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    };

    const isValidPassport = (passport?: string) => {
        if (!passport) return false;
        return /^[A-Z0-9]{6,9}$/i.test(passport.trim());
    };

    const isValidPinCode = (pin?: string) =>
        !!pin && /^[0-9]{5,6}$/.test(pin.trim());

    const validatePassengers = (): boolean => {
        const errors: Record<string, string> = {};

        passengers.forEach((pax, index) => {
            const prefix = `pax-${index}`;

            // Basic passenger fields
            if (!pax.FirstName.trim()) {
                errors[`${prefix}-FirstName`] = 'First name is required';
            }
            if (!pax.LastName.trim()) {
                errors[`${prefix}-LastName`] = 'Last name is required';
            }
            if (!pax.DateOfBirth) {
                errors[`${prefix}-DateOfBirth`] = 'Date of birth is required';
            }
            if (!pax.Nationality.trim()) {
                errors[`${prefix}-Nationality`] = 'Nationality is required';
            }
            if (!pax.CityOfResidence.trim()) {
                errors[`${prefix}-CityOfResidence`] = 'City of residence is required';
            }

            if (pax.IsLeadPax) {
                if (!pax.ContactNo.trim()) {
                    errors[`${prefix}-ContactNo`] = 'Contact number is required';
                } else if (!isValidPhone(pax.ContactNo)) {
                    errors[`${prefix}-ContactNo`] = 'Enter a valid 10-digit mobile number';
                }

                if (!pax.Email.trim()) {
                    errors[`${prefix}-Email`] = 'Email is required';
                } else if (!isValidEmail(pax.Email)) {
                    errors[`${prefix}-Email`] = 'Enter a valid email address';
                }
            }

            // Insurance-specific required fields
            if (includeInsurance) {
                if (!pax.PassportNo?.trim()) {
                    errors[`${prefix}-PassportNo`] = 'Passport number is required';
                } else if (!isValidPassport(pax.PassportNo)) {
                    errors[`${prefix}-PassportNo`] =
                        'Enter a valid passport number (6–9 alphanumeric characters)';
                }

                if (!pax.PassportCountry?.trim()) {
                    errors[`${prefix}-PassportCountry`] = 'Passport country is required';
                }

                if (!pax.AddressLine1?.trim()) {
                    errors[`${prefix}-AddressLine1`] = 'Address Line 1 is required';
                }

                if (!pax.CityCode?.trim()) {
                    errors[`${prefix}-CityCode`] = 'City is required';
                }

                if (!pax.CountryCode?.trim()) {
                    errors[`${prefix}-CountryCode`] = 'Country is required';
                }

                if (!pax.PinCode?.trim()) {
                    errors[`${prefix}-PinCode`] = 'PIN code is required';
                } else if (!isValidPinCode(pax.PinCode)) {
                    errors[`${prefix}-PinCode`] = 'Enter a valid PIN code';
                }

                if (!pax.BeneficiaryTitle?.trim()) {
                    errors[`${prefix}-BeneficiaryTitle`] = 'Beneficiary title is required';
                }

                if (!pax.BeneficiaryName?.trim()) {
                    errors[`${prefix}-BeneficiaryName`] = 'Beneficiary name is required';
                }

                if (!pax.RelationShipToInsured?.trim()) {
                    errors[`${prefix}-RelationShipToInsured`] =
                        'Relationship to insured is required';
                }

                if (!pax.RelationToBeneficiary?.trim()) {
                    errors[`${prefix}-RelationToBeneficiary`] =
                        'Relation to beneficiary is required';
                }

                if (!pax.MajorDestination?.trim()) {
                    errors[`${prefix}-MajorDestination`] = 'Major destination is required';
                }
            }
        });

        setFieldErrors(errors);

        if (Object.keys(errors).length > 0) {
            const firstFieldId = Object.keys(errors)[0];
            // Small delay so React renders error classes before scroll
            setTimeout(() => {
                const el = document.getElementById(firstFieldId);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    (el as HTMLElement).focus?.();
                }
            }, 0);
            return false;
        }

        return true;
    };

    const handlePassengerChange = (
        index: number,
        field: keyof PassengerFormData,
        value: string | number | boolean
    ) => {
        setPassengers(prev =>
            prev.map((passenger, i) => {
                if (i !== index) return passenger;

                let updatedPassenger: PassengerFormData = { ...passenger, [field]: value } as PassengerFormData;

                if (field === 'DateOfBirth' && typeof value === 'string' && value) {
                    const birthDate = dayjs(value);
                    const today = dayjs();
                    const age = today.diff(birthDate, 'year', true);

                    let paxType = 1; // Adult
                    if (age < 2) paxType = 3;       // Infant
                    else if (age >= 2 && age < 12) paxType = 2; // Child

                    updatedPassenger.PaxType = paxType;
                }

                if ((field === 'FirstName' || field === 'LastName' || field === 'Title') && includeInsurance) {
                    const beneficiaryName = `${updatedPassenger.Title} ${updatedPassenger.FirstName} ${updatedPassenger.LastName}`.trim();
                    updatedPassenger.BeneficiaryName = beneficiaryName;
                    updatedPassenger.BeneficiaryTitle = updatedPassenger.Title;
                }

                return updatedPassenger;
            })
        );

        // Clear error for this field when user changes it
        const key = `pax-${index}-${field}`;
        setFieldErrors(prev => {
            if (!prev[key]) return prev;
            const { [key]: _, ...rest } = prev;
            return rest;
        });
    };

    const getCurrentUserId = (): number | null => {
        const ssId = sessionStorage.getItem("user_id");
        if (ssId) return Number(ssId);

        try {
            const ssUser = JSON.parse(sessionStorage.getItem("user") || "null");
            if (ssUser?.id != null) return Number(ssUser.id);
        } catch { }

        try {
            const lsUser = JSON.parse(localStorage.getItem("user") || "null");
            if (lsUser?.id != null) {
                sessionStorage.setItem("user_id", String(lsUser.id));
                sessionStorage.setItem("user", JSON.stringify(lsUser));
                return Number(lsUser.id);
            }
        } catch { }

        return null;
    };

    const handleSavePassengers = async () => {
        setSavingPassengers(true);

        try {
            const userId = getCurrentUserId();
            if (!userId) {
                alert("Please log in to continue.");
                return;
            }
            // Validate all fields first
            const isValid = validatePassengers();
            if (!isValid) {
                setSavingPassengers(false);
                return;
            }

            const leadPax = passengers.find(p => p.IsLeadPax) || passengers[0];

            const flightOfferIds =
                (apiResponse?.FlightOfferIds || []).map(id => String(id));

            console.log('Saving passengers with UserId:', sessionStorage);

            const body: any = {
                FlightOfferId: flightOfferIds,            // array of strings
                UserId: Number(userId),        // numeric as in sample
                InsuranceFlag: includeInsurance ? 1 : 0,
                Passengers: passengers.map(pax => {
                    const basePassenger: any = {
                        Title: pax.Title,
                        FirstName: pax.FirstName.trim(),
                        LastName: pax.LastName.trim(),
                        PaxType: pax.PaxType,
                        Gender: pax.Gender,
                        DateOfBirth: pax.DateOfBirth,
                        Nationality: pax.Nationality,
                        ContactNo: leadPax?.ContactNo || pax.ContactNo,
                        Email: leadPax?.Email || pax.Email,
                        IsLeadPax: pax.IsLeadPax,
                        City: pax.CityOfResidence
                    };

                    if (includeInsurance) {
                        basePassenger.InsuranceData = {
                            BeneficiaryTitle: pax.BeneficiaryTitle || pax.Title,
                            BeneficiaryName: pax.BeneficiaryName,
                            RelationShipToInsured: pax.RelationShipToInsured || 'Self',
                            RelationToBeneficiary: pax.RelationToBeneficiary || 'Father',
                            AddressLine1: pax.AddressLine1,
                            MajorDestination: pax.MajorDestination,
                            CountryCode: pax.CountryCode || 'IND',
                            PassportCountry: pax.PassportCountry || pax.CountryCode || 'IND',
                            PassportNo: pax.PassportNo,
                            PinCode: pax.PinCode
                        };
                    }

                    return basePassenger;
                })
            };

            const response = await fetch(API_URLS.FLIGHTS.SAVE_PASSENGERS, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const data = await response.json();
            const nextRoute = isHoldMode ? routes.flightBooking : routes.flightAmenities;
            navigate(nextRoute, {
                state: {
                    savedPassengersDetails: data,
                    flightDetails: apiResponse,
                    previousRequestParams: location.state,
                    isRoundTrip: location.state?.isRoundTrip,
                    isHoldMode: location.state?.isHoldMode ?? false,
                    isDomestic,
                }
            });
        } catch (err: any) {
            // Show a user-friendly UI error, not an alert
            setError(err.message || 'Failed to save passenger details');
        } finally {
            setSavingPassengers(false);
        }
    };

    const HybridAirlineLogo = ({ airlineCode, airlineName, size = 40 }: {
        airlineCode: string;
        airlineName: string;
        size?: number;
    }) => {
        const [useFallback, setUseFallback] = useState(false);
        const cdnUrl = "https://images.kiwi.com/airlines/64";

        if (useFallback) {
            return (
                <div
                    className="rounded-circle d-flex align-items-center justify-content-center me-3 text-white fw-bold"
                    style={{
                        width: `${size}px`,
                        height: `${size}px`,
                        backgroundColor: '#3B82F6',
                        fontSize: `${size * 0.35}px`,
                        minWidth: `${size}px`
                    }}
                    title={airlineName}
                >
                    {airlineCode}
                </div>
            );
        }

        return (
            <img
                src={`${cdnUrl}/${airlineCode}.png`}
                className="rounded me-3"
                alt={airlineName}
                style={{ width: `${size}px`, height: `${size}px`, objectFit: 'contain' }}
                onError={() => setUseFallback(true)}
                loading="lazy"
            />
        );
    };

    const renderInsuranceSection = () => {
        if (!includeInsurance) return null;

        return (
            <div className="card shadow-none bg-light-200 mt-4">
                <div className="card-header">
                    <h5 className="mb-0">
                        <i className="isax isax-shield-tick me-2"></i>
                        Travel Insurance
                    </h5>
                </div>
                <div className="card-body">
                    {searchingInsurance ? (
                        <div className="text-center py-3">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading insurance options...</span>
                            </div>
                            <p className="mt-2">Loading insurance options...</p>
                        </div>
                    ) : insuranceError ? (
                        <div className="alert alert-warning">
                            <i className="isax isax-warning-2 me-2"></i>
                            {insuranceError}
                        </div>
                    ) : insurancePlans.length > 0 ? (
                        <div>
                            <div className="mb-3">
                                <label className="form-label fw-bold">Select Insurance Plan</label>
                                <div className="row">
                                    {insurancePlans.map((plan, index) => (
                                        <div key={plan.ResultIndex} className="col-md-6 mb-3">
                                            <div
                                                className={`card cursor-pointer ${selectedInsurancePlan?.ResultIndex === plan.ResultIndex ? 'border-primary bg-light' : ''}`}
                                                onClick={() => setSelectedInsurancePlan(plan)}
                                            >
                                                <div className="card-body">
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="insurancePlan"
                                                            checked={selectedInsurancePlan?.ResultIndex === plan.ResultIndex}
                                                            onChange={() => setSelectedInsurancePlan(plan)}
                                                        />
                                                        <label className="form-check-label fw-bold">
                                                            {plan.PlanName}
                                                        </label>
                                                    </div>
                                                    <div className="mt-2">
                                                        <p className="mb-1">
                                                            <strong>Coverage:</strong> ₹{plan.SumInsured}
                                                        </p>
                                                        <p className="mb-1">
                                                            <strong>Price:</strong>
                                                            <span className="text-primary fw-bold">
                                                                ₹{plan.Price?.OfferedPrice || plan.Price?.OfferedPriceRoundedOff}
                                                            </span>
                                                        </p>
                                                        <small className="text-muted">
                                                            {plan.PoweredBy || 'Travel Insurance'}
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Insurance Passenger Details */}
                            <div className="mt-4">
                                <h6 className="mb-3">Insurance Details for Passengers</h6>
                                {passengers.map((passenger, index) => {
                                    const prefix = `pax-${index}`;

                                    return (
                                        <div key={index} className="border rounded p-3 mb-3">
                                            <h6 className="text-primary">
                                                {passenger.IsLeadPax ? 'Lead Passenger' : `Passenger ${index + 1}`}
                                            </h6>

                                            <div className="row">
                                                <div className="col-md-3 mb-2">
                                                    <label className="form-label small">Beneficiary Title *</label>
                                                    <select
                                                        id={`${prefix}-BeneficiaryTitle`}
                                                        className={`form-control form-control-sm ${fieldErrors[`${prefix}-BeneficiaryTitle`] ? 'is-invalid' : ''}`}
                                                        value={passenger.BeneficiaryTitle || 'Mr'}
                                                        onChange={e => handlePassengerChange(index, 'BeneficiaryTitle', e.target.value)}
                                                    >
                                                        <option value="Mr">Mr</option>
                                                        <option value="Ms">Ms</option>
                                                        <option value="Mrs">Mrs</option>
                                                        <option value="Mstr">Mstr</option>
                                                    </select>
                                                    {fieldErrors[`${prefix}-BeneficiaryTitle`] && (
                                                        <div className="invalid-feedback">
                                                            {fieldErrors[`${prefix}-BeneficiaryTitle`]}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="col-md-9 mb-2">
                                                    <label className="form-label small">Beneficiary Name *</label>
                                                    <input
                                                        id={`${prefix}-BeneficiaryName`}
                                                        type="text"
                                                        className={`form-control form-control-sm ${fieldErrors[`${prefix}-BeneficiaryName`] ? 'is-invalid' : ''}`}
                                                        value={passenger.BeneficiaryName}
                                                        onChange={e => handlePassengerChange(index, 'BeneficiaryName', e.target.value)}
                                                    />
                                                    {fieldErrors[`${prefix}-BeneficiaryName`] && (
                                                        <div className="invalid-feedback">
                                                            {fieldErrors[`${prefix}-BeneficiaryName`]}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6 mb-2">
                                                    <label className="form-label small">Relationship to Insured *</label>
                                                    <select
                                                        id={`${prefix}-RelationShipToInsured`}
                                                        className={`form-control form-control-sm ${fieldErrors[`${prefix}-RelationShipToInsured`] ? 'is-invalid' : ''}`}
                                                        value={passenger.RelationShipToInsured || 'Self'}
                                                        onChange={e => handlePassengerChange(index, 'RelationShipToInsured', e.target.value)}
                                                    >
                                                        <option value="Self">Self</option>
                                                        <option value="Spouse">Spouse</option>
                                                        <option value="Child">Child</option>
                                                        <option value="Parent">Parent</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                    {fieldErrors[`${prefix}-RelationShipToInsured`] && (
                                                        <div className="invalid-feedback">
                                                            {fieldErrors[`${prefix}-RelationShipToInsured`]}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="col-md-6 mb-2">
                                                    <label className="form-label small">Relation to Beneficiary *</label>
                                                    <select
                                                        id={`${prefix}-RelationToBeneficiary`}
                                                        className={`form-control form-control-sm ${fieldErrors[`${prefix}-RelationToBeneficiary`] ? 'is-invalid' : ''}`}
                                                        value={passenger.RelationToBeneficiary || 'Father'}
                                                        onChange={e => handlePassengerChange(index, 'RelationToBeneficiary', e.target.value)}
                                                    >
                                                        <option value="Father">Father</option>
                                                        <option value="Mother">Mother</option>
                                                        <option value="Spouse">Spouse</option>
                                                        <option value="Son">Son</option>
                                                        <option value="Daughter">Daughter</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                    {fieldErrors[`${prefix}-RelationToBeneficiary`] && (
                                                        <div className="invalid-feedback">
                                                            {fieldErrors[`${prefix}-RelationToBeneficiary`]}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6 mb-2">
                                                    <label className="form-label small">Passport Number *</label>
                                                    <input
                                                        id={`${prefix}-PassportNo`}
                                                        type="text"
                                                        inputMode="text"
                                                        autoCapitalize="characters"
                                                        maxLength={9}
                                                        className={`form-control form-control-sm ${fieldErrors[`${prefix}-PassportNo`] ? 'is-invalid' : ''
                                                            }`}
                                                        value={passenger.PassportNo}
                                                        onChange={(e) =>
                                                            handlePassengerChange(
                                                                index,
                                                                'PassportNo',
                                                                e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
                                                            )
                                                        }
                                                    />
                                                    {fieldErrors[`${prefix}-PassportNo`] && (
                                                        <div className="invalid-feedback">
                                                            {fieldErrors[`${prefix}-PassportNo`]}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="col-md-6 mb-2">
                                                    <label className="form-label small">Passport Country *</label>
                                                    <input
                                                        id={`${prefix}-PassportCountry`}
                                                        type="text"
                                                        className={`form-control form-control-sm ${fieldErrors[`${prefix}-PassportCountry`] ? 'is-invalid' : ''}`}
                                                        value={passenger.PassportCountry}
                                                        onChange={e => handlePassengerChange(index, 'PassportCountry', e.target.value)}
                                                    />
                                                    {fieldErrors[`${prefix}-PassportCountry`] && (
                                                        <div className="invalid-feedback">
                                                            {fieldErrors[`${prefix}-PassportCountry`]}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6 mb-2">
                                                    <label className="form-label small">Address Line 1 *</label>
                                                    <input
                                                        id={`${prefix}-AddressLine1`}
                                                        type="text"
                                                        className={`form-control form-control-sm ${fieldErrors[`${prefix}-AddressLine1`] ? 'is-invalid' : ''}`}
                                                        value={passenger.AddressLine1}
                                                        onChange={e => handlePassengerChange(index, 'AddressLine1', e.target.value)}
                                                    />
                                                    {fieldErrors[`${prefix}-AddressLine1`] && (
                                                        <div className="invalid-feedback">
                                                            {fieldErrors[`${prefix}-AddressLine1`]}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="col-md-6 mb-2">
                                                    <label className="form-label small">Address Line 2</label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={passenger.AddressLine2}
                                                        onChange={e => handlePassengerChange(index, 'AddressLine2', e.target.value)}
                                                    />
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-4 mb-2">
                                                    <label className="form-label small">City *</label>
                                                    <input
                                                        id={`${prefix}-CityCode`}
                                                        type="text"
                                                        className={`form-control form-control-sm ${fieldErrors[`${prefix}-CityCode`] ? 'is-invalid' : ''}`}
                                                        value={passenger.CityCode}
                                                        onChange={e => handlePassengerChange(index, 'CityCode', e.target.value)}
                                                    />
                                                    {fieldErrors[`${prefix}-CityCode`] && (
                                                        <div className="invalid-feedback">
                                                            {fieldErrors[`${prefix}-CityCode`]}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="col-md-4 mb-2">
                                                    <label className="form-label small">Country *</label>
                                                    <input
                                                        id={`${prefix}-CountryCode`}
                                                        type="text"
                                                        className={`form-control form-control-sm ${fieldErrors[`${prefix}-CountryCode`] ? 'is-invalid' : ''}`}
                                                        value={passenger.CountryCode}
                                                        onChange={e => handlePassengerChange(index, 'CountryCode', e.target.value)}
                                                    />
                                                    {fieldErrors[`${prefix}-CountryCode`] && (
                                                        <div className="invalid-feedback">
                                                            {fieldErrors[`${prefix}-CountryCode`]}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="col-md-4 mb-2">
                                                    <label className="form-label small">PIN Code *</label>
                                                    <input
                                                        id={`${prefix}-PinCode`}
                                                        type="text"
                                                        className={`form-control form-control-sm ${fieldErrors[`${prefix}-PinCode`] ? 'is-invalid' : ''}`}
                                                        value={passenger.PinCode}
                                                        onChange={e => handlePassengerChange(index, 'PinCode', e.target.value)}
                                                    />
                                                    {fieldErrors[`${prefix}-PinCode`] && (
                                                        <div className="invalid-feedback">
                                                            {fieldErrors[`${prefix}-PinCode`]}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6 mb-2">
                                                    <label className="form-label small">Major Destination *</label>
                                                    <input
                                                        id={`${prefix}-MajorDestination`}
                                                        type="text"
                                                        className={`form-control form-control-sm ${fieldErrors[`${prefix}-MajorDestination`] ? 'is-invalid' : ''}`}
                                                        value={passenger.MajorDestination}
                                                        onChange={e => handlePassengerChange(index, 'MajorDestination', e.target.value)}
                                                    />
                                                    {fieldErrors[`${prefix}-MajorDestination`] && (
                                                        <div className="invalid-feedback">
                                                            {fieldErrors[`${prefix}-MajorDestination`]}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-3 text-muted">
                            No insurance plans available for your trip.
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderPassengerForm = () => (
        <div className="card shadow-none bg-light-200 mt-4">
            <div className="card-header">
                <h5 className="mb-0">
                    <i className="isax isax-user me-2"></i>
                    Passenger Details
                </h5>
            </div>
            <div className="card-body">
                {/* Insurance Checkbox */}
                <div className="mb-4 p-3 border rounded bg-white">
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="includeInsurance"
                            checked={includeInsurance}
                            onChange={(e) => setIncludeInsurance(e.target.checked)}
                            disabled={searchingInsurance}
                        />
                        <label className="form-check-label fw-bold" htmlFor="includeInsurance">
                            <i className="isax isax-shield-tick me-2 text-primary"></i>
                            Add Travel Insurance
                        </label>
                    </div>
                    <small className="text-muted ms-4">
                        Protect your trip with comprehensive travel insurance coverage
                    </small>
                    {includeInsurance && selectedInsurancePlan && (
                        <div className="mt-2 ms-4">
                            <div className="alert alert-info py-2 mb-0">
                                <strong>Selected:</strong> {selectedInsurancePlan.PlanName} - ₹
                                {selectedInsurancePlan.SumInsured} coverage for ₹
                                {selectedInsurancePlan.Price?.OfferedPrice ||
                                    selectedInsurancePlan.Price?.OfferedPriceRoundedOff}
                            </div>
                        </div>
                    )}
                </div>

                {/* Insurance extra details section */}
                {renderInsuranceSection()}

                {/* Passenger forms */}
                {passengers.map((passenger, index) => {
                    const prefix = `pax-${index}`;

                    return (
                        <div key={index} className="passenger-form mb-4 pb-4 border-bottom">
                            <h6 className="mb-3 text-primary">
                                {passenger.IsLeadPax ? 'Lead Passenger' : `Passenger ${index + 1}`}
                                {passenger.IsLeadPax && (
                                    <span className="badge bg-primary ms-2">Lead</span>
                                )}
                            </h6>

                            <div className="row">
                                {/* Title */}
                                <div className="col-md-3 mb-3">
                                    <label className="form-label" htmlFor={`${prefix}-Title`}>
                                        Title *
                                    </label>
                                    <select
                                        id={`${prefix}-Title`}
                                        className="form-control"
                                        value={passenger.Title}
                                        onChange={(e) =>
                                            handlePassengerChange(index, 'Title', e.target.value)
                                        }
                                    >
                                        <option value="Mr">Mr</option>
                                        <option value="Ms">Ms</option>
                                        <option value="Mrs">Mrs</option>
                                        <option value="Mstr">Mstr</option>
                                    </select>
                                </div>

                                {/* First Name */}
                                <div className="col-md-4 mb-3">
                                    <label
                                        className="form-label"
                                        htmlFor={`${prefix}-FirstName`}
                                    >
                                        First Name *
                                    </label>
                                    <input
                                        id={`${prefix}-FirstName`}
                                        type="text"
                                        autoComplete="given-name"
                                        className={`form-control ${fieldErrors[`${prefix}-FirstName`] ? 'is-invalid' : ''
                                            }`}
                                        value={passenger.FirstName}
                                        onChange={(e) =>
                                            handlePassengerChange(
                                                index,
                                                'FirstName',
                                                e.target.value
                                            )
                                        }
                                    />
                                    {fieldErrors[`${prefix}-FirstName`] && (
                                        <div className="invalid-feedback">
                                            {fieldErrors[`${prefix}-FirstName`]}
                                        </div>
                                    )}
                                </div>

                                {/* Last Name */}
                                <div className="col-md-5 mb-3">
                                    <label
                                        className="form-label"
                                        htmlFor={`${prefix}-LastName`}
                                    >
                                        Last Name *
                                    </label>
                                    <input
                                        id={`${prefix}-LastName`}
                                        type="text"
                                        autoComplete="family-name"
                                        className={`form-control ${fieldErrors[`${prefix}-LastName`] ? 'is-invalid' : ''
                                            }`}
                                        value={passenger.LastName}
                                        onChange={(e) =>
                                            handlePassengerChange(
                                                index,
                                                'LastName',
                                                e.target.value
                                            )
                                        }
                                    />
                                    {fieldErrors[`${prefix}-LastName`] && (
                                        <div className="invalid-feedback">
                                            {fieldErrors[`${prefix}-LastName`]}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="row">
                                {/* Gender */}
                                <div className="col-md-6 mb-3">
                                    <label
                                        className="form-label"
                                        htmlFor={`${prefix}-Gender`}
                                    >
                                        Gender *
                                    </label>
                                    <select
                                        id={`${prefix}-Gender`}
                                        className="form-control"
                                        value={passenger.Gender}
                                        onChange={(e) =>
                                            handlePassengerChange(
                                                index,
                                                'Gender',
                                                parseInt(e.target.value)
                                            )
                                        }
                                    >
                                        <option value={1}>Male</option>
                                        <option value={2}>Female</option>
                                        <option value={3}>Other</option>
                                    </select>
                                </div>

                                {/* Date of Birth */}
                                <div className="col-md-6 mb-3">
                                    <label
                                        className="form-label"
                                        htmlFor={`${prefix}-DateOfBirth`}
                                    >
                                        Date of Birth *
                                    </label>
                                    <DatePicker
                                        id={`${prefix}-DateOfBirth`}
                                        className={`form-control w-100 ${fieldErrors[`${prefix}-DateOfBirth`]
                                            ? 'is-invalid'
                                            : ''
                                            }`}
                                        value={
                                            passenger.DateOfBirth
                                                ? dayjs(passenger.DateOfBirth)
                                                : null
                                        }
                                        onChange={(date) =>
                                            handlePassengerChange(
                                                index,
                                                'DateOfBirth',
                                                date ? date.format('YYYY-MM-DD') : ''
                                            )
                                        }
                                        format="YYYY-MM-DD"
                                    />
                                    {fieldErrors[`${prefix}-DateOfBirth`] && (
                                        <div className="invalid-feedback d-block">
                                            {fieldErrors[`${prefix}-DateOfBirth`]}
                                        </div>
                                    )}
                                </div>

                                {/* Nationality */}
                                <div className="col-md-6 mb-3">
                                    <label
                                        className="form-label"
                                        htmlFor={`${prefix}-Nationality`}
                                    >
                                        Nationality *
                                    </label>
                                    <input
                                        id={`${prefix}-Nationality`}
                                        type="text"
                                        className={`form-control ${fieldErrors[`${prefix}-Nationality`]
                                            ? 'is-invalid'
                                            : ''
                                            }`}
                                        value={passenger.Nationality}
                                        onChange={(e) =>
                                            handlePassengerChange(
                                                index,
                                                'Nationality',
                                                e.target.value
                                            )
                                        }
                                    />
                                    {fieldErrors[`${prefix}-Nationality`] && (
                                        <div className="invalid-feedback">
                                            {fieldErrors[`${prefix}-Nationality`]}
                                        </div>
                                    )}
                                </div>

                                {/* City of Residence */}
                                <div className="col-md-6 mb-3">
                                    <label
                                        className="form-label"
                                        htmlFor={`${prefix}-CityOfResidence`}
                                    >
                                        City of Residence *
                                    </label>
                                    <input
                                        id={`${prefix}-CityOfResidence`}
                                        type="text"
                                        className={`form-control ${fieldErrors[`${prefix}-CityOfResidence`]
                                            ? 'is-invalid'
                                            : ''
                                            }`}
                                        value={passenger.CityOfResidence}
                                        onChange={(e) =>
                                            handlePassengerChange(
                                                index,
                                                'CityOfResidence',
                                                e.target.value
                                            )
                                        }
                                    />
                                    {fieldErrors[`${prefix}-CityOfResidence`] && (
                                        <div className="invalid-feedback">
                                            {fieldErrors[`${prefix}-CityOfResidence`]}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Lead passenger contact details */}
                            {passenger.IsLeadPax && (
                                <div className="row">
                                    {/* Contact Number */}
                                    <div className="col-md-6 mb-3">
                                        <label
                                            className="form-label"
                                            htmlFor={`${prefix}-ContactNo`}
                                        >
                                            Contact Number *
                                        </label>
                                        <input
                                            id={`${prefix}-ContactNo`}
                                            type="tel"
                                            maxLength={10}
                                            autoComplete="tel-national"
                                            className={`form-control ${fieldErrors[`${prefix}-ContactNo`]
                                                ? 'is-invalid'
                                                : ''
                                                }`}
                                            value={passenger.ContactNo}
                                            onChange={(e) =>
                                                handlePassengerChange(
                                                    index,
                                                    'ContactNo',
                                                    e.target.value
                                                )
                                            }
                                        />
                                        {fieldErrors[`${prefix}-ContactNo`] && (
                                            <div className="invalid-feedback">
                                                {fieldErrors[`${prefix}-ContactNo`]}
                                            </div>
                                        )}
                                    </div>

                                    {/* Email Address */}
                                    <div className="col-md-6 mb-3">
                                        <label
                                            className="form-label"
                                            htmlFor={`${prefix}-Email`}
                                        >
                                            Email Address *
                                        </label>
                                        <input
                                            id={`${prefix}-Email`}
                                            type="email"
                                            autoComplete="email"
                                            className={`form-control ${fieldErrors[`${prefix}-Email`]
                                                ? 'is-invalid'
                                                : ''
                                                }`}
                                            value={passenger.Email}
                                            onChange={(e) =>
                                                handlePassengerChange(
                                                    index,
                                                    'Email',
                                                    e.target.value
                                                )
                                            }
                                        />
                                        {fieldErrors[`${prefix}-Email`] && (
                                            <div className="invalid-feedback">
                                                {fieldErrors[`${prefix}-Email`]}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}

                <div className="text-end">
                    <button
                        className="btn btn-primary btn-lg"
                        onClick={handleSavePassengers}
                        disabled={savingPassengers}
                    >
                        {savingPassengers ? 'Processing...' : 'Review & Continue'}
                    </button>
                </div>
            </div>
        </div>
    );

    const getSelectedOption = (resultIndex?: string): SelectedOption | null => {
        if (!apiResponse?.Fare?.SelectedOptions?.length) return null;

        // For one-way flights, return the first selected option
        if (!location.state?.isRoundTrip) {
            return apiResponse.Fare.SelectedOptions[0];
        }

        // For round trips, find the option matching the result index
        return apiResponse.Fare.SelectedOptions.find(opt =>
            opt.optionId === location.state.selectedOutboundOption?.optionId ||
            opt.optionId === location.state.selectedReturnOption?.optionId
        ) || apiResponse.Fare.SelectedOptions[0];
    };

    const getTotalFare = (): number => {
        if (!apiResponse?.Fare) return 0;

        let total = 0;

        if (location.state?.isRoundTrip) {
            // Prefer explicit per-leg totals when available; fall back to the
            // combined OfferedFare from the backend (set when no option was pre-selected)
            const outboundFare = location.state.selectedOutboundOption?.totalFare || 0;
            const returnFare   = location.state.selectedReturnOption?.totalFare   || 0;
            total = (outboundFare + returnFare) || apiResponse.Fare.OfferedFare || 0;
        } else {
            total = apiResponse.Fare.OfferedFare || 0;
        }

        // Add insurance cost if selected
        if (includeInsurance && selectedInsurancePlan) {
            total += (selectedInsurancePlan.Price?.OfferedPrice || selectedInsurancePlan.Price?.OfferedPriceRoundedOff || 0);
        }

        return total;
    };

    const renderFlightSegment = (segment: Segment, index: number, isReturn = false) => {
        return (
            <div key={index} className="flight-segment mb-4">
                <div className="segment-header d-flex align-items-center mb-3">
                    <HybridAirlineLogo
                        airlineCode={segment.Airline.AirlineCode}
                        airlineName={segment.Airline.AirlineName}
                        size={40}
                    />
                    <div>
                        <h6 className="mb-1">
                            {segment.Airline.AirlineName} - {segment.Airline.FlightNumber}
                            {isReturn && <span className="badge bg-secondary ms-2">Return</span>}
                        </h6>
                        <p className="text-muted mb-0">{segment.Craft}</p>
                    </div>
                </div>

                <div className="flight-info-grid">
                    <div className="flight-info-item">
                        <h6>Departure</h6>
                        <p>{dayjs(segment.Origin.DepTime).format('DD MMM YYYY HH:mm')}</p>
                        <small>
                            {segment.Origin.Airport.AirportName} ({segment.Origin.Airport.AirportCode})
                            {segment.Origin.Airport.Terminal && ` • Terminal ${segment.Origin.Airport.Terminal}`}
                        </small>
                    </div>

                    <div className="flight-info-item">
                        <h6>Arrival</h6>
                        <p>{dayjs(segment.Destination.ArrTime).format('DD MMM YYYY HH:mm')}</p>
                        <small>
                            {segment.Destination.Airport.AirportName} ({segment.Destination.Airport.AirportCode})
                            {segment.Destination.Airport.Terminal && ` • Terminal ${segment.Destination.Airport.Terminal}`}
                        </small>
                    </div>

                    <div className="flight-info-item">
                        <h6>Duration</h6>
                        <p>{`${Math.floor(segment.Duration / 60)}h ${segment.Duration % 60}m`}</p>
                    </div>

                    <div className="flight-info-item">
                        <h6>Class</h6>
                        <p>{segment.Airline.FareClass}</p>
                    </div>
                </div>

                <div className="baggage-info mt-3">
                    <small className="text-muted">
                        <strong>Baggage:</strong> {segment.Baggage} •
                        <strong> Cabin:</strong> {segment.CabinBaggage}
                    </small>
                </div>
            </div>
        );
    };

    const renderFlightInformation = () => {
        if (!apiResponse || !apiResponse.Fare || !apiResponse.Fare.Results?.length) {
            return <div className="alert alert-warning">No flight data available</div>;
        }

        const isRoundTrip = location.state?.isRoundTrip;
        const isInternationalCombined = location.state?.isInternationalCombined;
        const isMultiCity = location.state?.isMultiCity;
        const outboundFlight = apiResponse.Fare.Results[0];
        // Domestic round trip:     Results[0] = outbound, Results[1] = return (different FareQuote calls)
        // International combined:  Results[0] = Results[1] (same combined ticket);
        //                          return leg is in Segments[1], picked via isInternationalCombined below.
        const returnFlight = isRoundTrip
            ? (isInternationalCombined ? apiResponse.Fare.Results[0] : apiResponse.Fare.Results[1])
            : null;
        const selectedOption = getSelectedOption();

        return (
            <>
                {/* Flight Header */}
                <div className="service-wrap slider-wrap-five mb-4">
                    <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
                        <div className="mb-2">
                            <h4 className="mb-1 d-flex align-items-center flex-wrap">
                                {isRoundTrip ? 'Round Trip Flight' : isMultiCity ? 'Multi-City Flight' : 'One Way Flight'}
                                <span className="badge badge-xs bg-success rounded-pill mb-2 ms-2">
                                    <i className="isax isax-ticket-star5 me-1" />
                                    Verified
                                </span>
                            </h4>
                            <div className="d-flex align-items-center flex-wrap">
                                <p className="fs-14 mb-2 me-3 pe-3 border-end d-flex align-items-center">
                                    <HybridAirlineLogo
                                        airlineCode={outboundFlight.AirlineCode}
                                        airlineName={outboundFlight.AirlineName || ""}
                                        size={40}
                                    />
                                    {outboundFlight.AirlineName}
                                </p>
                                {isRoundTrip && returnFlight && (
                                    <p className="fs-14 mb-2 me-3 pe-3 border-end d-flex align-items-center">
                                        <HybridAirlineLogo
                                            airlineCode={returnFlight.AirlineCode}
                                            airlineName={returnFlight.AirlineName || ""}
                                            size={40}
                                        />
                                        {returnFlight.AirlineName}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="accordion custom-accordion accordion-shadow-none">
                    {isMultiCity ? (
                        /* Multi-city: render every Segments group as a numbered leg */
                        outboundFlight.Segments.map((segGroup, legIndex) => (
                            <div key={legIndex} className={`flight-details-card${legIndex > 0 ? ' mt-4' : ''}`}>
                                <h5 className="text-lg font-semibold">
                                    <i className="isax isax-airplane me-2 text-blue-500"></i>
                                    Flight {legIndex + 1}
                                    {segGroup[0] && (
                                        <span className="fs-14 fw-normal text-muted ms-2">
                                            {segGroup[0].Origin.Airport.AirportCode} → {segGroup[segGroup.length - 1].Destination.Airport.AirportCode}
                                        </span>
                                    )}
                                </h5>
                                {segGroup.map((segment, index) =>
                                    renderFlightSegment(segment, index)
                                )}
                            </div>
                        ))
                    ) : (
                        <>
                            {/* Outbound Flight */}
                            <div className="flight-details-card">
                                <h5 className="text-lg font-semibold">
                                    <i className="isax isax-airplane me-2 text-blue-500"></i>
                                    Outbound Flight
                                </h5>
                                {outboundFlight.Segments[0]?.map((segment, index) =>
                                    renderFlightSegment(segment, index)
                                )}
                            </div>

                            {/* Return Flight */}
                            {isRoundTrip && returnFlight && (
                                <div className="flight-details-card mt-4">
                                    <h5 className="text-lg font-semibold">
                                        <i className="isax isax-airplane me-2 text-green-500"></i>
                                        Return Flight
                                    </h5>
                                    {returnFlight.Segments[isInternationalCombined ? 1 : 0]?.map((segment, index) =>
                                        renderFlightSegment(segment, index, true)
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {/* Fare Details */}
                    <div className="flight-details-card">
                        <h5 className="text-lg font-semibold">
                            <i className="isax isax-coin me-2 text-green-500"></i>
                            Fare Details
                        </h5>
                        <div className="fare-details-grid">
                            <div>
                                <div className="fare-summary-item">
                                    <span className="text-gray-600">Base Fare</span>
                                    <span className="font-semibold">
                                        ₹{apiResponse.Fare.OriginalFare?.toLocaleString()}
                                    </span>
                                </div>
                                {apiResponse.Fare.OptionsTotal > 0 && (
                                    <div className="fare-summary-item">
                                        <span className="text-gray-600">Amenities</span>
                                        <span className="font-semibold">
                                            +₹{apiResponse.Fare.OptionsTotal?.toLocaleString()}
                                        </span>
                                    </div>
                                )}
                                {includeInsurance && selectedInsurancePlan && (
                                    <div className="fare-summary-item">
                                        <span className="text-gray-600">Travel Insurance</span>
                                        <span className="font-semibold">
                                            +₹{(selectedInsurancePlan.Price?.OfferedPrice || selectedInsurancePlan.Price?.OfferedPriceRoundedOff)?.toLocaleString()}
                                        </span>
                                    </div>
                                )}
                                <div className="fare-total">
                                    <div className="flex justify-between items-center">
                                        <span className="text-lg font-medium">Total</span>
                                        <span className="text-xl font-bold text-blue-600">
                                            ₹{getTotalFare()?.toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {selectedOption && (
                                <div className="fare-option-box">
                                    <h6>Selected Option: {selectedOption.name}</h6>
                                    <p>{selectedOption.description}</p>
                                    {(selectedOption.amenities?.length ?? 0) > 0 && (
                                        <div>
                                            <h6 className="font-medium text-blue-700 mb-2">Includes:</h6>
                                            {selectedOption.amenities?.map((amenity, idx) => (
                                                <div key={idx} className="amenity-item">
                                                    <span className="text-gray-700">{amenity.description}</span>
                                                    <span className="font-medium">+₹{amenity.pricePerPax}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Baggage Information */}
                    <div className="accordion-item border-0 mb-4">
                        <div className="accordion-header">
                            <button className="accordion-button" type="button" onClick={() => toggleSection('baggageInfo')}>
                                <i className="isax isax-bag me-2"></i>
                                Baggage Information
                            </button>
                        </div>
                        <div className={`accordion-collapse collapse${expandedSections.baggageInfo ? ' show' : ''}`}>
                            <div className="accordion-body pt-0">
                                {isMultiCity ? (
                                    /* Multi-city: one section per leg */
                                    outboundFlight.Segments.map((segGroup, legIndex) => (
                                        <div key={legIndex} className="mb-3">
                                            <h6>Flight {legIndex + 1}</h6>
                                            {segGroup.map((segment, segIndex) => (
                                                <div key={segIndex} className="mb-2">
                                                    <p><strong>Flight {segment.Airline.FlightNumber}:</strong></p>
                                                    <p>Checked Baggage: {segment.Baggage || 'N/A'}</p>
                                                    <p>Cabin Baggage: {segment.CabinBaggage || 'N/A'}</p>
                                                </div>
                                            ))}
                                        </div>
                                    ))
                                ) : (
                                    apiResponse.Fare.Results.map((flight, flightIndex) => {
                                        // Combined international tickets: Results[0] and Results[1] are the
                                        // same FareQuote response. Return leg is in Segments[1], not Segments[0].
                                        const segIdx = (isInternationalCombined && flightIndex === 1) ? 1 : 0;
                                        return (
                                            <div key={flightIndex} className="mb-3">
                                                <h6>{flightIndex === 0 ? 'Outbound' : 'Return'} Flight</h6>
                                                {flight.Segments[segIdx]?.map((segment, segIndex) => (
                                                    <div key={segIndex} className="mb-2">
                                                        <p><strong>Flight {segment.Airline.FlightNumber}:</strong></p>
                                                        <p>Checked Baggage: {segment.Baggage || 'N/A'}</p>
                                                        <p>Cabin Baggage: {segment.CabinBaggage || 'N/A'}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })
                                )}

                                {selectedOption?.amenities?.some(a => a.type === 'Baggage') && (
                                    <div className="mt-3">
                                        <h6>Additional Baggage Options:</h6>
                                        {selectedOption.amenities
                                            ?.filter(a => a.type === 'Baggage')
                                            .map((baggage, idx) => (
                                                <div key={idx} className="d-flex justify-content-between border-bottom pb-2 mb-2">
                                                    <span>{baggage.description}</span>
                                                    <span>₹{baggage.pricePerPax}</span>
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Fare Rules */}
                    {(apiResponse.FareRules?.length ?? 0) > 0 && (
                        <div className="accordion-item border-0 mb-4">
                            <div className="accordion-header">
                                <button className="accordion-button" type="button" onClick={() => toggleSection('fareRules')}>
                                    <i className="isax isax-document-text me-2"></i>
                                    Fare Rules
                                </button>
                            </div>
                            <div className={`accordion-collapse collapse${expandedSections.fareRules ? ' show' : ''}`}>
                                <div className="accordion-body pt-0">
                                    {apiResponse.FareRules?.map((rule, idx) => (
                                        <div key={idx} className="mb-3">
                                            <h6>{rule.Airline} - {rule.Origin} to {rule.Destination}</h6>
                                            <div dangerouslySetInnerHTML={{ __html: rule.FareRuleDetail }} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Insurance Options */}
                    {(apiResponse.Insurance?.length ?? 0) > 0 && (
                        <div className="accordion-item border-0 mb-4">
                            <div className="accordion-header">
                                <button className="accordion-button" type="button" onClick={() => toggleSection('insurance')}>
                                    <i className="isax isax-brifecase-tick me-2"></i>
                                    Insurance Options
                                </button>
                            </div>
                            <div className={`accordion-collapse collapse${expandedSections.insurance ? ' show' : ''}`}>
                                <div className="accordion-body pt-0">
                                    {apiResponse.Insurance?.map((insurance, idx) => (
                                        <div key={idx} className="mb-3 border-bottom pb-3">
                                            <h6>{insurance.PlanName}</h6>
                                            <p>Coverage: ₹{insurance.SumInsured}</p>
                                            <small className="text-muted">
                                                Price: ₹{insurance.Price?.OfferedPrice}
                                            </small>
                                            <div className="mt-2">
                                                <h6>Coverage Details:</h6>
                                                <ul className="small">
                                                    {insurance.CoverageDetails?.slice(0, 3).map((coverage, cIdx) => (
                                                        <li key={cIdx}>{coverage.Coverage}: ₹{coverage.SumInsured}</li>
                                                    ))}
                                                    {(insurance.CoverageDetails?.length ?? 0) > 3 && (
                                                        <li>+{(insurance.CoverageDetails?.length ?? 0) - 3} more coverages</li>
                                                    )}
                                                </ul>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </>
        );
    };

    if (loading) {
        return (
            <div>
                <div className={`breadcrumb-bar flightList-banner-bg text-center`}>
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12 col-12">
                                <h2 className="breadcrumb-title mb-2">Flight Details</h2>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flight-loading">
                    <AirplaneLoader text="Loading flight information..." size="large" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <div className={`breadcrumb-bar flightList-banner-bg text-center`}>
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12 col-12">
                                <h2 className="breadcrumb-title mb-2">Flight Details</h2>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flight-error">
                    <div className="error-content">
                        <i className="isax isax-close-circle"></i>
                        <h4>Something went wrong</h4>
                        <p>{error}</p>
                        <button
                            className="btn btn-primary mt-3"
                            onClick={() => navigate(-1)}
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className={`breadcrumb-bar flightList-banner-bg text-center`}>
                <div className="container">
                    <div className="row">
                        <div className="col-md-12 col-12">
                            <h2 className="breadcrumb-title mb-2">Flight Details</h2>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container">
                    <div className="row">
                        <div className="col-xl-8">
                            {renderFlightInformation()}
                            {renderPassengerForm()}
                        </div>

                        <div className="col-xl-4">
                            {/* Price Summary Card */}
                            <div className="card shadow-none sticky-top">
                                <div className="card-body">
                                    <h5 className="mb-3">
                                        {isHoldMode ? (
                                            <><i className="isax isax-lock me-2 text-warning"></i>Lock Price Summary</>
                                        ) : 'Price Summary'}
                                    </h5>
                                    {apiResponse && apiResponse.Fare && (
                                        <div className="fare-summary">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span>Base Fare</span>
                                                <span>₹{apiResponse.Fare.OriginalFare?.toLocaleString()}</span>
                                            </div>
                                            {apiResponse.Fare.OptionsTotal > 0 && (
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span>Amenities</span>
                                                    <span>+₹{apiResponse.Fare.OptionsTotal?.toLocaleString()}</span>
                                                </div>
                                            )}
                                            {includeInsurance && selectedInsurancePlan && (
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span>Travel Insurance</span>
                                                    <span>
                                                        +₹{(selectedInsurancePlan.Price?.OfferedPrice || selectedInsurancePlan.Price?.OfferedPriceRoundedOff)?.toLocaleString()}
                                                    </span>
                                                </div>
                                            )}
                                            <hr />
                                            <div className="d-flex justify-content-between fw-bold mb-2">
                                                <span>Total Fare</span>
                                                <span className="text-primary">
                                                    ₹{getTotalFare()?.toLocaleString()}
                                                </span>
                                            </div>

                                            {isHoldMode && (() => {
                                                const lastTicketDate = apiResponse.Fare.Results?.[0]?.LastTicketDate;
                                                if (!lastTicketDate || lastTicketDate.startsWith('0001-01-01')) return null;
                                                const formattedDeadline = new Date(lastTicketDate).toLocaleString('en-IN', {
                                                    weekday: 'short', day: 'numeric', month: 'short',
                                                    year: 'numeric', hour: '2-digit', minute: '2-digit',
                                                });
                                                return (
                                                    <div
                                                        className="d-flex align-items-start gap-2 rounded p-2 mb-2"
                                                        style={{ background: '#fff3cd', border: '1px solid #ffc107' }}
                                                    >
                                                        <i className="isax isax-calendar-1 text-warning mt-1 flex-shrink-0"></i>
                                                        <div>
                                                            <p className="mb-0 fw-semibold fs-13" style={{ color: '#92400e' }}>Last Date to Book</p>
                                                            <p className="mb-0 fs-12" style={{ color: '#78350f' }}>{formattedDeadline}</p>
                                                        </div>
                                                    </div>
                                                );
                                            })()}

                                            {isHoldMode && (() => {
                                                const passengerCount = location.state?.PassengerCount || 1;
                                                const holdDeposit = depositPerPax * passengerCount;
                                                const remaining = getTotalFare() - holdDeposit;
                                                return (
                                                    <>
                                                        <hr />
                                                        <div className="alert alert-warning p-2 mb-2 fs-13">
                                                            <i className="isax isax-lock me-1"></i>
                                                            <strong>Lock Price Mode</strong>
                                                            <p className="mb-0 fs-12 mt-1">Pay a small deposit now to lock this fare. Complete payment before the hold deadline.</p>
                                                        </div>
                                                        <div className="d-flex justify-content-between mb-1 text-success fw-semibold">
                                                            <span>🔒 Pay Now (Deposit)</span>
                                                            <span>₹{holdDeposit.toLocaleString()}</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between mb-2 text-muted fs-13">
                                                            <span>Pay Later (Remaining)</span>
                                                            <span>₹{remaining.toLocaleString()}</span>
                                                        </div>
                                                        <div className="fs-12 text-muted">
                                                            ₹{depositPerPax.toLocaleString()} × {passengerCount} passenger{passengerCount > 1 ? 's' : ''}
                                                        </div>
                                                        <hr className="my-3" />
                                                        <p className="fw-semibold fs-12 text-dark mb-2">Important to Know</p>
                                                        <div className="d-flex align-items-start gap-2 rounded-2 p-2" style={{ background: "#fef2f2" }}>
                                                            <span style={{ fontSize: 15, flexShrink: 0 }}>🚫</span>
                                                            <span className="fs-11 text-muted">Lock deposit is <strong>non-refundable</strong> &amp; non-adjustable against the booking.</span>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FlightDetails;